import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

import toastConfig from '../../../../components/shared/constants/toaster-config';
import { applyTaskUpdateToFocusAction } from '../../../../redux/taskFocusSync';
import { receivesHumanTasks } from '../access';
import { TASK_OFFERED, TASK_REMOVED, TASK_UPDATED } from '../constants/realtime-events';
import { minutesLeft, toEpochMs } from '../helpers/time';
import {
  addTaskNotification,
  emitHumanTaskEvent,
  presentTaskOfferInChat,
  removeTaskNotification,
  withdrawTaskOfferInChat,
} from './human-tasks-events';

/**
 * Pusher handlers of Inicio (spec §7), dispatched from DashboardLayout's
 * `notificationActions` on ANY screen (S11: the toast has to reach the agent
 * wherever they are). None of them throws on a malformed payload, and all of
 * them do nothing for a user who does not receive tasks.
 *
 * In the chat (focus mode, spec S3) the open chat shows the offer itself, with
 * «Tomar» (see `setChatTaskOfferPresenter`), and `taskUpdated` keeps the focus
 * list current (redux/taskFocusSync.js).
 */

const taskIdOf = (data) => (data && typeof data.taskId === 'string' && data.taskId) || null;

/** "Te llegó una nueva tarea. Tienes N minutos para tomarla." — no countdown for a human-only task. */
export const offeredTaskToastMessage = (data, now = Date.now()) => {
  const humanOnly = !data || data.aiCanDo === false;
  const minutes = humanOnly ? null : minutesLeft(data.expiresAt, now);
  if (minutes === null) return 'Te llegó una nueva tarea.';
  return `Te llegó una nueva tarea. Tienes ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'} para tomarla.`;
};

const textOrNull = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

/** What the in-chat toast needs from a `taskOffered` payload (no instruction, no PII beyond the name it shows). */
const chatOfferOf = (data, taskId, expiresAt) => ({
  taskId,
  clientId: textOrNull(data.clientId),
  clientName: textOrNull(data.clientName),
  label: textOrNull(data.label),
  aiCanDo: data.aiCanDo !== false,
  expiresAt,
});

/** The open chat shows the offer; without one, or if it fails, the generic toast does. */
const announceOffer = (offer, message) => {
  try {
    if (presentTaskOfferInChat(offer)) return;
  } catch (error) {
    Sentry.captureException(error, { tags: { component: 'humanTasks', op: 'chatOfferToast' } });
  }
  toast.info(message, toastConfig);
};

/**
 * `{ taskId, expiresAt, aiCanDo, clientId, clientName, label, instruction }`
 * on `agent_<userId>`: a new task in the agent's list.
 */
export const taskOfferedReceived = (data) => (dispatch, getState) => {
  const taskId = taskIdOf(data);
  if (!taskId || !receivesHumanTasks(getState().user?.data)) return;

  const receivedAt = Date.now();
  const message = offeredTaskToastMessage(data, receivedAt);
  const expiresAt = toEpochMs(data.expiresAt);
  const isNew = addTaskNotification({
    taskId,
    aiCanDo: data.aiCanDo !== false,
    expiresAt,
    message,
    receivedAt,
  });
  if (isNew) announceOffer(chatOfferOf(data, taskId, expiresAt), message);
  emitHumanTaskEvent(TASK_OFFERED, data);
};

/**
 * `{ taskId, status, clientId, channelId }`: the context or the state of a
 * task changed. Refetch, move its conversation in or out of the focus list,
 * and close its offer in the chat once it is no longer on offer.
 */
export const taskUpdatedReceived = (data) => (dispatch, getState) => {
  if (!receivesHumanTasks(getState().user?.data)) return;
  dispatch(applyTaskUpdateToFocusAction(data));
  if (data && data.status && data.status !== 'visible') withdrawTaskOfferInChat(taskIdOf(data));
  emitHumanTaskEvent(TASK_UPDATED, data);
};

/**
 * Expired, reassigned or discarded: out of the list and out of the notices.
 * The payload has no conversation, so the focus list follows the refetch.
 */
export const taskRemovedReceived = (data) => (dispatch, getState) => {
  if (!receivesHumanTasks(getState().user?.data)) return;
  const taskId = taskIdOf(data);
  if (taskId) {
    removeTaskNotification(taskId);
    withdrawTaskOfferInChat(taskId);
  }
  emitHumanTaskEvent(TASK_REMOVED, data);
};

/** For DashboardLayout's `notificationActions`. */
export const humanTaskNotificationActions = {
  [TASK_OFFERED]: taskOfferedReceived,
  [TASK_UPDATED]: taskUpdatedReceived,
  [TASK_REMOVED]: taskRemovedReceived,
};
